import { Component, signal, HostListener, OnDestroy, OnInit, inject } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { filter } from 'rxjs';
import { ApiService, showToast, toastState } from './api.service';
import { InstallAppComponent } from './install-app.component';
import { PwaInstallService } from './pwa-install.service';

interface MenuItem {
  label: string;
  section?: string;
  icon?: string;
  route?: string;
  exact?: boolean;
  queryParams?: Record<string, string>;
  children?: MenuItem[];
  officeAdminOnly?: boolean;
}

@Component({
  selector: 'app-admin-layout',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, RouterOutlet, CommonModule, FormsModule, InstallAppComponent],
  template: `
    <div class="min-h-dvh flex font-sans admin-premium-bg">

      <!-- Mobile Overlay -->
      @if (isMobileSidebarOpen()) {
        <button class="fixed inset-0 bg-slate-900/40 z-30 md:hidden border-none outline-none w-full h-full cursor-default" (click)="toggleMobileSidebar()" aria-label="Close sidebar"></button>
      }

      <!-- Global Toast Notifications -->
      @if (toastState().show) {
        <div class="fixed top-3 left-3 right-3 md:top-6 md:left-auto md:right-6 z-[100] animate-in slide-in-from-top-4 fade-in duration-300">
          <div class="bg-white rounded-2xl shadow-[0_10px_40px_rgba(27,34,44,0.12)] border p-4 pr-6 flex items-start gap-4 w-full md:min-w-[320px] md:max-w-md relative overflow-hidden" 
               [class]="toastState().type === 'success' ? 'border-emerald-100' : 'border-red-100'">
            <div class="absolute left-0 top-0 bottom-0 w-1.5" [class]="toastState().type === 'success' ? 'bg-emerald-500' : 'bg-red-500'"></div>
            <div class="w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5" 
                 [class]="toastState().type === 'success' ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'">
              <i class="ti text-lg" [class]="toastState().type === 'success' ? 'ti-check' : 'ti-alert-circle'"></i>
            </div>
            <div>
              <h4 class="text-sm font-bold text-slate-900">{{ toastState().type === 'success' ? 'Success' : 'Error' }}</h4>
              <p class="text-xs text-slate-600 mt-0.5 leading-relaxed">{{ toastState().message }}</p>
            </div>
            <button (click)="toastState.set({show: false, type: toastState().type, message: ''})" class="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"><i class="ti ti-x"></i></button>
          </div>
        </div>
      }

      <!-- LEFT SIDEBAR — Warely style: light background, pill active nav, user card at bottom -->
      <aside
        class="admin-sidebar flex flex-col shrink-0 transition-all duration-300 ease-in-out fixed md:sticky md:top-0 md:self-start z-40 h-dvh"
        style="background:#1f4772;"
        [class.-translate-x-full]="!isMobileSidebarOpen()"
        [class.translate-x-0]="isMobileSidebarOpen()"
        [class.md:translate-x-0]="true"
        [class.is-collapsed]="isCollapsed()">

        <!-- Collapse Toggle -->
        <button
          (click)="toggleSidebar()"
          class="hidden md:flex absolute -right-3 top-5 w-6 h-6 bg-white border border-slate-200 rounded-full items-center justify-center text-slate-400 shadow-sm hover:text-[#2B9AD2] z-50 transition-colors cursor-pointer">
          <i class="ti text-[16px]" [class]="isCollapsed() ? 'ti-chevron-right' : 'ti-chevron-left'"></i>
        </button>

        <!-- Logo Area -->
        <div class="h-[72px] flex items-center px-5 shrink-0 overflow-hidden">
          <a routerLink="/admin/dashboard" (click)="closeMobileSidebar()" class="flex items-center min-w-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-white/30" aria-label="Go to Command Center">
            <img [src]="isCollapsed() ? '/logo-sm.png' : '/logo-dark.png'" alt="NZ Plumbing" class="max-h-12 w-auto shrink-0" [class.mr-3]="!isCollapsed()" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';">
            <div class="items-center hidden">
              <div class="w-10 h-10 rounded-2xl flex items-center justify-center shrink-0" style="background:linear-gradient(135deg,#2B9AD2,#5EC9F0);" [class.mr-3]="!isCollapsed()">
                <svg viewBox="0 0 24 24" class="w-5 h-5 fill-white" xmlns="http://www.w3.org/2000/svg"><path d="M12 2C12 2 5 8.5 5 14a7 7 0 0014 0c0-5.5-7-12-7-12z"/></svg>
              </div>
              @if (!isCollapsed()) {
                <div class="flex flex-col">
                  <span class="font-bold text-white tracking-tight text-sm leading-tight">NZ Plumbing</span>
                  <span class="text-[10px] text-white/70 tracking-widest uppercase">Limited</span>
                </div>
              }
            </div>
          </a>
          <button type="button" (click)="closeMobileSidebar()" class="ml-auto md:hidden w-9 h-9 rounded-lg bg-white/10 text-white/80 flex items-center justify-center">
            <i class="ti ti-x text-[20px]"></i>
          </button>
        </div>

        <!-- Navigation -->
        <nav class="flex-1 py-2 px-2 space-y-0.5" [ngClass]="isCollapsed() ? 'overflow-visible' : 'overflow-y-auto overflow-x-hidden'">
          @for (menu of visibleMenus(); track menu.label) {
            @if (!isCollapsed() && menu.section) {
              <div class="px-3 pt-3 pb-1.5 text-[9px] font-bold uppercase tracking-[0.16em] text-white/32">
                {{ menu.section }}
              </div>
            }
            @if (!menu.children) {
              <a [routerLink]="menu.route" [queryParams]="menu.queryParams" (click)="closeMobileSidebar()"
                 routerLinkActive #rla="routerLinkActive"
                 [routerLinkActiveOptions]="menu.exact ? {exact: true} : {exact: false}"
                  class="flex items-center gap-2.5 px-3 py-2 rounded-md transition-colors group relative"
                 [ngClass]="rla.isActive
                   ? 'bg-[#0f5f8f] text-white'
                   : 'text-white/58 hover:bg-white/8 hover:text-white'"
                 [title]="isCollapsed() ? menu.label : ''">
                <i class="ti shrink-0 w-4 h-4 overflow-visible text-[15px] text-center" [class]="'ti-' + menu.icon"
                     [ngClass]="rla.isActive ? 'text-white' : 'text-white/55 group-hover:text-white'"></i>
                @if (!isCollapsed()) {
                   <span class="font-semibold text-[11px] whitespace-nowrap">{{menu.label}}</span>
                }
              </a>
            } @else {
              <div class="relative group/menu">
                <button (click)="toggleSubmenu(menu.label)"
                        [title]="isCollapsed() ? menu.label : ''"
                        class="w-full flex items-center justify-between px-3 py-2 rounded-md transition-colors text-white/58 hover:bg-white/8 hover:text-white cursor-pointer"
                        [class.bg-[#0f5f8f]]="expandedMenus[menu.label] && !isCollapsed()"
                        [class.text-white]="expandedMenus[menu.label] && !isCollapsed()">
                  <div class="flex items-center gap-2.5">
                    <i class="ti shrink-0 w-4 h-4 text-white/55 overflow-visible text-[15px] text-center" [class]="'ti-' + menu.icon" [class.!text-white]="expandedMenus[menu.label]"></i>
                    @if (!isCollapsed()) { <span class="font-semibold text-[11px] whitespace-nowrap">{{menu.label}}</span> }
                  </div>
                  @if (!isCollapsed()) {
                    <i class="ti ti-chevron-down text-white/45 transition-transform text-[14px]" [class.!text-white]="expandedMenus[menu.label]" [class.rotate-180]="expandedMenus[menu.label]"></i>
                  }
                </button>

                <!-- Inline submenu -->
                @if (!isCollapsed() && expandedMenus[menu.label]) {
                  <div class="mt-1 ml-4 pl-2 space-y-0.5 border-l border-white/10">
                    @for (child of menu.children; track child.label) {
              <a [routerLink]="child.route" [queryParams]="child.queryParams" (click)="closeMobileSidebar()"
                         routerLinkActive #rlaChild="routerLinkActive"
                         [routerLinkActiveOptions]="child.queryParams ? { paths: 'exact', queryParams: 'exact', fragment: 'ignored', matrixParams: 'ignored' } : { paths: 'subset', queryParams: 'ignored', fragment: 'ignored', matrixParams: 'ignored' }"
                         class="flex items-center pl-3 pr-3 py-1.5 rounded-md transition-colors text-[11px]"
                         [ngClass]="rlaChild.isActive
                           ? 'bg-[#164f78] text-white font-semibold'
                           : 'text-white/52 hover:bg-white/8 hover:text-white'">
                        <span class="w-1.5 h-1.5 rounded-full mr-2 shrink-0"
                              [ngClass]="rlaChild.isActive ? 'bg-white' : 'bg-white/28'"></span>
                        {{child.label}}
                      </a>
                    }
                  </div>
                }

                <!-- Collapsed flyout -->
                @if (isCollapsed()) {
                  <div class="absolute left-full top-0 ml-2 w-52 bg-white border border-slate-200 rounded-xl shadow-xl py-2 hidden group-hover/menu:block z-50">
                    <div class="px-4 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100 mb-1">{{menu.label}}</div>
                    @for (child of menu.children; track child.label) {
                    <a [routerLink]="child.route" [queryParams]="child.queryParams" routerLinkActive="text-[#1A6FA6] bg-[#EAF5FA] font-semibold" class="flex items-center px-4 py-2 text-[13px] text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors">
                        {{child.label}}
                      </a>
                    }
                  </div>
                }
              </div>
            }
          }
        </nav>


      </aside>

      <!-- MAIN CANVAS -->
      <div class="flex-1 flex flex-col min-w-0 min-h-dvh">

        <!-- TOP BAR — clean, pill search, welcome greeting -->
        <header class="sticky top-0 z-20 h-[56px] md:h-[48px] bg-white flex items-center justify-between px-3 md:px-5 shrink-0" style="border-bottom: 1px solid #E8EDF2;">
          <div class="flex items-center gap-3 min-w-0">
            <!-- Mobile hamburger -->
            <button class="md:hidden w-8 h-8 flex items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 cursor-pointer" (click)="toggleMobileSidebar()" aria-label="Open sidebar">
              <i class="ti ti-menu text-[18px]"></i>
            </button>
            <!-- Pill search -->
            <div class="relative hidden sm:block w-[280px] lg:w-[360px]" (click)="$event.stopPropagation()">
              <i class="ti ti-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[15px]"></i>
              <input type="text" placeholder="Search jobs, customers, quotes..."
                [ngModel]="globalSearchQuery()"
                (ngModelChange)="onGlobalSearchInput($event)"
                (focus)="openGlobalSearch()"
                (keydown.enter)="openFirstSearchResult()"
                (keydown.escape)="closeGlobalSearch()"
                class="w-full h-8 pl-8 pr-3 rounded-md bg-slate-50 border border-slate-200 focus:bg-white focus:border-[#2B9AD2] focus:ring-1 focus:ring-[#2B9AD2]/20 outline-none text-[11px] transition-all placeholder:text-slate-400">
              @if (globalSearchOpen()) {
                <div class="absolute left-0 top-[38px] w-[min(520px,calc(100vw-32px))] bg-white border border-slate-100 rounded-2xl shadow-[0_18px_48px_rgba(15,23,42,0.16)] z-[1000] overflow-hidden">
                  <div class="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-3">
                    <div>
                      <p class="text-[10px] font-bold text-slate-400 uppercase tracking-[0.14em]">Global search</p>
                      <p class="text-[11px] text-slate-500 mt-0.5">{{ globalSearchHint() }}</p>
                    </div>
                    @if (isGlobalSearchLoading()) {
                      <i class="ti ti-loader-2 animate-spin text-slate-400"></i>
                    }
                  </div>
                  <div class="max-h-[420px] overflow-y-auto py-1">
                    @if (globalSearchQuery().trim().length < 2) {
                      <div class="px-4 py-8 text-center">
                        <p class="text-sm font-semibold text-slate-900">Start typing</p>
                        <p class="text-xs text-slate-500 mt-1">Search jobs, customers, quotes, projects, POs and work orders.</p>
                      </div>
                    } @else if (!isGlobalSearchLoading() && globalSearchResults().length === 0) {
                      <div class="px-4 py-8 text-center">
                        <p class="text-sm font-semibold text-slate-900">No results found</p>
                        <p class="text-xs text-slate-500 mt-1">Try a customer name, job number, address, quote or PO number.</p>
                      </div>
                    } @else {
                      @for (group of groupedGlobalSearchResults(); track group.label) {
                        <div class="px-3 py-2">
                          <p class="px-1 pb-1 text-[10px] font-bold text-slate-400 uppercase tracking-[0.14em]">{{ group.label }}</p>
                          @for (item of group.items; track item.type + '-' + item.id) {
                            <button type="button" (click)="openSearchResult(item)" class="w-full flex items-start gap-3 px-2.5 py-2 rounded-xl hover:bg-slate-50 text-left transition-colors">
                              <span class="w-8 h-8 rounded-lg bg-[#EAF5FA] text-[#1A6FA6] flex items-center justify-center shrink-0">
                                <i class="ti text-[16px]" [class]="'ti-' + item.icon"></i>
                              </span>
                              <span class="min-w-0 flex-1">
                                <span class="block text-[12px] font-bold text-slate-900 truncate">{{ item.title }}</span>
                                <span class="block text-[10px] text-slate-500 truncate mt-0.5">{{ item.subtitle || 'Open record' }}</span>
                              </span>
                              @if (item.badge) {
                                <span class="px-2 py-1 rounded-md bg-slate-100 text-slate-500 text-[9px] font-bold uppercase shrink-0">{{ item.badge }}</span>
                              }
                            </button>
                          }
                        </div>
                      }
                    }
                  </div>
                </div>
              }
            </div>
          </div>

          <div class="flex items-center gap-2">
            <a routerLink="/admin/dispatch/unassigned"
               class="hidden md:flex h-8 px-4 rounded-md bg-white border border-slate-200 text-slate-700 text-[11px] font-semibold hover:bg-slate-50 transition-colors items-center justify-center">
              Dispatch Board
            </a>
            <a routerLink="/admin/financials/quotes"
               class="hidden lg:flex h-8 px-4 rounded-md bg-white border border-slate-200 text-slate-700 text-[11px] font-semibold hover:bg-slate-50 transition-colors items-center justify-center">
              Financials
            </a>
            <!-- Notification bell -->
            <div class="relative">
              <button type="button" (click)="toggleNotifications()" class="relative w-8 h-8 flex items-center justify-center rounded-md bg-white border border-slate-200 hover:bg-slate-50 text-slate-500 transition-colors cursor-pointer outline-none" aria-label="Notifications">
                <i class="ti ti-bell text-[16px]"></i>
                @if (unreadNotificationCount() > 0) {
                  <span class="absolute -top-1 -right-1 min-w-4 h-4 px-1 bg-red-500 rounded-full text-white text-[9px] leading-4 font-bold text-center">{{ unreadNotificationCount() > 9 ? '9+' : unreadNotificationCount() }}</span>
                }
              </button>
              @if (notificationsOpen()) {
                <div class="absolute right-0 top-[42px] w-[min(360px,calc(100vw-24px))] bg-white/98 rounded-2xl border border-slate-100 py-2 z-[1000] shadow-[0_18px_48px_rgba(15,23,42,0.14)] overflow-hidden backdrop-blur-xl">
                  <div class="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-3">
                    <div>
                      <p class="text-[10px] font-bold text-slate-400 uppercase tracking-[0.14em]">Notifications</p>
                      <p class="text-xs text-slate-500 mt-0.5">{{ unreadNotificationCount() }} unread</p>
                    </div>
                    <button type="button" (click)="markAllNotificationsRead()" [disabled]="unreadNotificationCount() === 0" class="h-7 px-2.5 rounded-md bg-slate-50 text-slate-500 text-[10px] font-bold hover:bg-slate-100 disabled:opacity-40">
                      Mark all read
                    </button>
                  </div>
                  <div class="max-h-[380px] overflow-y-auto py-1">
                    @for (item of notifications(); track item.id) {
                      <a [routerLink]="notificationRoute(item)" (click)="openNotification(item)" class="flex items-start gap-3 px-4 py-3 hover:bg-slate-50 transition-colors border-l-2" [ngClass]="item.read_at ? 'border-transparent' : 'border-[#1A6FA6]'">
                        <span class="w-8 h-8 rounded-md shrink-0 flex items-center justify-center" [ngClass]="notificationIconClass(item)">
                          <i class="ti text-[16px]" [class]="'ti-' + notificationIcon(item)"></i>
                        </span>
                        <span class="min-w-0 flex-1">
                          <span class="block text-[12px] font-bold text-slate-900 truncate">{{ item.title }}</span>
                          <span class="block text-[10px] text-slate-500 mt-0.5 line-clamp-2">{{ item.body || 'Open for details.' }}</span>
                          <span class="block text-[10px] text-slate-400 mt-1">{{ notificationTime(item.created_at) }}</span>
                        </span>
                        @if (!item.read_at) {
                          <span class="w-2 h-2 rounded-full bg-[#1A6FA6] mt-1 shrink-0"></span>
                        }
                      </a>
                    } @empty {
                      <div class="px-4 py-8 text-center">
                        <p class="text-sm font-semibold text-slate-900">No notifications</p>
                      <p class="text-xs text-slate-500 mt-1">Workflow alerts will appear here.</p>
                      </div>
                    }
                  </div>
                  <div class="px-3 pt-2 border-t border-slate-100">
                    <a routerLink="/admin/notifications" (click)="notificationsOpen.set(false)" class="h-9 w-full rounded-md bg-[#F5FBFE] text-[#1A6FA6] text-[11px] font-bold flex items-center justify-center gap-2 hover:bg-[#E8F4FB]">
                      <i class="ti ti-layout-list text-[14px]"></i>
                      Open notification center
                    </a>
                  </div>
                </div>
              }
            </div>
            <!-- Settings -->
            <a routerLink="/admin/settings" class="hidden sm:flex w-8 h-8 items-center justify-center rounded-md bg-white border border-slate-200 hover:bg-slate-50 text-slate-500 transition-colors cursor-pointer outline-none" aria-label="Settings">
              <i class="ti ti-settings text-[16px]"></i>
            </a>
            <a routerLink="/admin/dispatch/unassigned" [queryParams]="{ create: 'job' }"
               class="hidden sm:flex h-8 px-4 rounded-md bg-[#0C3A5A] hover:bg-[#082d46] text-white text-[11px] font-bold shadow-sm transition-colors items-center gap-2">
              <i class="ti ti-plus text-[14px]"></i>
              <span>New Job</span>
            </a>
            <!-- Top Bar Profile Dropdown (Premium) -->
            <div class="relative">
              <button (click)="toggleProfileDropdown()" 
                      class="flex items-center gap-2 h-8 pl-1 pr-2 bg-white hover:bg-slate-50 rounded-md border border-transparent transition-colors cursor-pointer group outline-none text-left">
                <!-- Avatar -->
                <div class="w-7 h-7 rounded-full flex items-center justify-center font-bold text-[11px] text-white shrink-0"
                     style="background:#0C3A5A;">
                  {{ currentUserInitials() }}
                </div>
                <!-- User greeting -->
                <div class="hidden xl:flex flex-col items-start min-w-[88px]">
                  <span class="text-[11px] font-bold text-slate-800 leading-tight max-w-[110px] truncate">{{ currentUserName() }}</span>
                  <span class="text-[9px] text-slate-400 font-semibold leading-tight">{{ currentUserRole() }}</span>
                </div>
              </button>

              <!-- Dropdown Menu -->
              @if (profileDropdownOpen()) {
                <div class="absolute right-0 top-[42px] w-56 bg-white/95 rounded-2xl border border-white/80 py-2 z-[1000] shadow-[0_18px_48px_rgba(15,23,42,0.12)] overflow-hidden backdrop-blur-xl">
                  <div class="px-5 pt-2 pb-2.5 border-b border-slate-100 border-dashed">
                    <p class="text-[10px] font-bold text-slate-400 uppercase tracking-[0.14em]">Account</p>
                  </div>
                  <a routerLink="/admin/profile" (click)="profileDropdownOpen.set(false)" class="flex items-center h-10 px-5 text-[13px] text-slate-700 hover:bg-[#F5FBFE] hover:text-[#2B9AD2] transition-colors gap-3 font-semibold group">
                    <i class="ti ti-user text-[18px] text-slate-400 group-hover:text-[#2B9AD2] transition-colors"></i>
                    <span>Profile</span>
                  </a>
                  <a routerLink="/admin/settings" (click)="profileDropdownOpen.set(false)" class="flex items-center h-10 px-5 text-[13px] text-slate-700 hover:bg-[#F5FBFE] hover:text-[#2B9AD2] transition-colors gap-3 font-semibold group">
                    <i class="ti ti-settings text-[18px] text-slate-400 group-hover:text-[#2B9AD2] transition-colors"></i>
                    <span>Settings</span>
                  </a>
                  @if (!install.installed()) {
                    <button type="button" (click)="installAdminApp()" class="w-full h-10 flex items-center px-5 text-[13px] text-slate-700 hover:bg-[#F5FBFE] hover:text-[#2B9AD2] transition-colors gap-3 font-semibold cursor-pointer text-left group">
                      <i class="ti ti-device-mobile-down text-[18px] text-slate-400 group-hover:text-[#2B9AD2] transition-colors"></i>
                      <span>Install Admin App</span>
                    </button>
                  }
                  <button type="button" (click)="clearCacheAndRefresh()" class="w-full h-10 flex items-center px-5 text-[13px] text-slate-700 hover:bg-[#F5FBFE] hover:text-[#2B9AD2] transition-colors gap-3 font-semibold cursor-pointer text-left group">
                    <i class="ti ti-refresh-dot text-[18px] text-slate-400 group-hover:text-[#2B9AD2] transition-colors"></i>
                    <span>Clear Cache & Refresh</span>
                  </button>
                  <div class="border-t border-slate-100 my-1.5 border-dashed"></div>
                  <button type="button" (click)="logout()" class="w-full h-10 flex items-center px-5 text-[13px] text-red-600 hover:bg-red-50 transition-colors gap-3 font-semibold cursor-pointer text-left">
                    <i class="ti ti-logout text-[18px] text-red-500"></i>
                    <span>Logout</span>
                  </button>
                </div>
              }
            </div>
          </div>
        </header>

        <!-- Router Outlet -->
        <div class="flex-1 min-w-0 admin-premium-bg pb-[74px] md:pb-0">
          <router-outlet></router-outlet>
        </div>
      </div>

      <nav class="md:hidden fixed bottom-0 left-0 right-0 z-30 bg-white/95 backdrop-blur-xl border-t border-slate-200 px-2 pb-safe h-[66px] grid grid-cols-5 shadow-[0_-8px_24px_rgba(15,23,42,0.08)]">
        <a routerLink="/admin/dashboard" routerLinkActive="text-[#1A6FA6]" [routerLinkActiveOptions]="{exact: true}" class="admin-mobile-tab">
          <i class="ti ti-layout-dashboard text-[20px]"></i>
          <span>Home</span>
        </a>
        <a routerLink="/admin/dispatch/allocated" routerLinkActive="text-[#1A6FA6]" class="admin-mobile-tab">
          <i class="ti ti-list-details text-[20px]"></i>
          <span>Dispatch</span>
        </a>
        <a routerLink="/admin/customers" routerLinkActive="text-[#1A6FA6]" class="admin-mobile-tab">
          <i class="ti ti-users text-[20px]"></i>
          <span>Customers</span>
        </a>
        <a routerLink="/admin/financials/materials" routerLinkActive="text-[#1A6FA6]" class="admin-mobile-tab">
          <i class="ti ti-package text-[20px]"></i>
          <span>Stock</span>
        </a>
        <button type="button" (click)="toggleMobileSidebar()" class="admin-mobile-tab">
          <i class="ti ti-menu-2 text-[20px]"></i>
          <span>More</span>
        </button>
      </nav>
      <app-install-app />
    </div>
  `
})
export class AdminLayoutComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  api = inject(ApiService);
  install = inject(PwaInstallService);
  isCollapsed = signal(false);
  isMobileSidebarOpen = signal(false);
  profileDropdownOpen = signal(false);
  notificationsOpen = signal(false);
  notifications = signal<any[]>([]);
  unreadNotificationCount = signal(0);
  globalSearchQuery = signal('');
  globalSearchResults = signal<any[]>([]);
  isGlobalSearchLoading = signal(false);
  globalSearchOpen = signal(false);
  expandedMenus: Record<string, boolean> = {};
  toastState = toastState;
  private notificationTimer: ReturnType<typeof setInterval> | null = null;
  private globalSearchTimer: ReturnType<typeof setTimeout> | null = null;
  private globalSearchRequestId = 0;

  menus: MenuItem[] = [
    { label: 'Command Center', section: 'Operations', icon: 'layout-dashboard', route: '/admin/dashboard', exact: true },
    { label: 'Notification Center', icon: 'bell-ringing', route: '/admin/notifications', exact: true },
    { label: 'Customers', icon: 'users', route: '/admin/customers' },
    { label: 'Customer Feedback', icon: 'message-star', route: '/admin/customer-feedback' },
    {
      label: 'Dispatch Board',
      icon: 'list-details',
      children: [
        { label: 'Jobs Control', route: '/admin/dispatch/control' },
        { label: 'Unassigned', route: '/admin/dispatch/unassigned' },
        { label: 'Allocated / In Progress', route: '/admin/dispatch/allocated' },
        { label: 'Ready for Office', route: '/admin/dispatch/ready' },
        { label: 'Archived Jobs', route: '/admin/dispatch/archived' },
      ]
    },
    {
      label: 'Workforce Operations',
      icon: 'briefcase-2',
      children: [
        { label: 'Operations Command', route: '/admin/operations/report' },
        { label: 'Coverage Calendar', route: '/admin/operations/calendar' },
        { label: 'Staff Profiles', route: '/admin/operations/staff' },
        { label: 'Timesheet Control', route: '/admin/operations/timesheet' },
        { label: 'Leave Requests', route: '/admin/operations/leave-requests' },
        { label: 'Workers Control', route: '/admin/operations/workers-control' },
        { label: 'Fleet Status', route: '/admin/operations/fleet' },
      ]
    },
    {
      label: 'Financials',
      section: 'Business',
      icon: 'file-text',
      children: [
        { label: 'Quotes', route: '/admin/financials/quotes' },
        { label: 'Purchase Orders', route: '/admin/financials/purchase-orders' },
        { label: 'Work Orders', route: '/admin/financials/work-orders' },
        { label: 'PO Suppliers', route: '/admin/financials/purchase-orders/suppliers' },
        { label: 'PO Templates', route: '/admin/financials/purchase-orders/templates' },
        { label: 'PO Reports', route: '/admin/financials/purchase-orders/reports' },
        { label: 'Materials', route: '/admin/financials/materials' },
      ]
    },
    {
      label: 'Development Projects',
      icon: 'building-skyscraper',
      children: [
        { label: 'Active Projects',    route: '/admin/projects', queryParams: { status: 'active' } },
        { label: 'Completed Projects', route: '/admin/projects', queryParams: { status: 'completed' } },
      ]
    },
    {
      label: 'Forms',
      icon: 'forms',
      children: [
        { label: 'PS3 Plumbing', route: '/admin/forms/ps3-plumbing' },
        { label: 'PS3 Drainage', route: '/admin/forms/ps3-drainage' },
        { label: 'Gas Safety', route: '/admin/forms/gas-safety' },
      ]
    },
    {
      label: 'AI Portal',
      section: 'Platform',
      icon: 'sparkles',
      children: [
        { label: 'Insurance Doc', route: '/admin/ai/insurance' },
        { label: 'As Built Doc', route: '/admin/ai/as-built' },
        { label: 'As Built V2', route: '/admin/as-built-v2' },
        { label: 'As Built Training', route: '/admin/ai/as-built/training' },
      ]
    },
    { label: 'Documents', icon: 'folders', route: '/admin/documents', officeAdminOnly: true },
    { label: 'Compress PDF', icon: 'file-zip', route: '/admin/tools/compress-pdf' },
    {
      label: 'Compliance Hub',
      icon: 'shield-check',
      children: [
        { label: 'Visa Tracker', route: '/admin/compliance/visas' },
        { label: 'Audits', route: '/admin/compliance/audits' },
      ]
    },
    { label: 'Company Settings', icon: 'settings', route: '/admin/settings', exact: true },
    { label: 'Notification Controls', icon: 'adjustments-horizontal', route: '/admin/notification-controls', exact: true }
  ];

  ngOnInit() {
    this.checkScreenSize();
    if (this.api.isAuthenticated()) {
      this.api.getProfile().subscribe({ error: () => {} });
      this.loadNotifications();
      this.notificationTimer = setInterval(() => this.loadNotifications(false), 45000);
    }
    this.syncExpandedMenuWithRoute(this.router.url);
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(event => this.syncExpandedMenuWithRoute(event.urlAfterRedirects));
  }

  ngOnDestroy() {
    if (this.notificationTimer) clearInterval(this.notificationTimer);
  }

  @HostListener('window:resize')
  onResize() {
    this.checkScreenSize();
  }

  @HostListener('document:click')
  onDocumentClick() {
    this.closeGlobalSearch();
  }

  checkScreenSize() {
    if (typeof window !== 'undefined') {
      const width = window.innerWidth;
      if (width >= 768 && width < 1024) {
        this.isCollapsed.set(true);
      }
    }
  }

  toggleSidebar() { this.isCollapsed.update(v => !v); }
  toggleMobileSidebar() { this.isMobileSidebarOpen.update(v => !v); }
  closeMobileSidebar() { this.isMobileSidebarOpen.set(false); }

  onGlobalSearchInput(value: string) {
    this.globalSearchQuery.set(value);
    this.globalSearchOpen.set(true);
    if (this.globalSearchTimer) clearTimeout(this.globalSearchTimer);

    const query = value.trim();
    if (query.length < 2) {
      this.globalSearchResults.set([]);
      this.isGlobalSearchLoading.set(false);
      return;
    }

    this.isGlobalSearchLoading.set(true);
    this.globalSearchTimer = setTimeout(() => this.runGlobalSearch(query), 250);
  }

  openGlobalSearch() {
    this.globalSearchOpen.set(true);
  }

  closeGlobalSearch() {
    this.globalSearchOpen.set(false);
  }

  runGlobalSearch(query: string) {
    const requestId = ++this.globalSearchRequestId;
    this.api.globalSearch(query).subscribe({
      next: (res: any) => {
        if (requestId !== this.globalSearchRequestId) return;
        this.isGlobalSearchLoading.set(false);
        this.globalSearchResults.set(res?.success ? (res.data?.items || []) : []);
      },
      error: () => {
        if (requestId !== this.globalSearchRequestId) return;
        this.isGlobalSearchLoading.set(false);
        this.globalSearchResults.set([]);
      }
    });
  }

  groupedGlobalSearchResults(): Array<{ label: string; items: any[] }> {
    const groups = new Map<string, any[]>();
    for (const item of this.globalSearchResults()) {
      const label = item.type_label || 'Results';
      groups.set(label, [...(groups.get(label) || []), item]);
    }
    return Array.from(groups.entries()).map(([label, items]) => ({ label, items }));
  }

  globalSearchHint(): string {
    const count = this.globalSearchResults().length;
    if (this.globalSearchQuery().trim().length < 2) return 'Type at least two characters';
    if (this.isGlobalSearchLoading()) return 'Searching records...';
    return count === 1 ? '1 result' : `${count} results`;
  }

  openFirstSearchResult() {
    const first = this.globalSearchResults()[0];
    if (first) this.openSearchResult(first);
  }

  openSearchResult(item: any) {
    this.closeGlobalSearch();
    this.globalSearchQuery.set('');
    this.globalSearchResults.set([]);
    this.router.navigate([item.route || '/admin/dashboard'], { queryParams: item.queryParams || undefined });
  }

  toggleSubmenu(label: string) {
    if (!this.isCollapsed()) {
      this.expandedMenus[label] = !this.expandedMenus[label];
    }
  }

  private syncExpandedMenuWithRoute(url: string) {
    const activeParent = this.findActiveParentLabel(url);
    const nextState: Record<string, boolean> = {};

    for (const menu of this.menus) {
      if (menu.children) {
        nextState[menu.label] = menu.label === activeParent;
      }
    }

    this.expandedMenus = nextState;
  }

  private findActiveParentLabel(url: string): string | null {
    const path = url.split('?')[0].split('#')[0];

    if (path.startsWith('/admin/dispatch/')) return 'Dispatch Board';
    if (path.startsWith('/admin/projects/') || path.startsWith('/admin/project-pdf/') || path.startsWith('/admin/project-doc/')) return 'Development Projects';

    for (const menu of this.menus) {
      if (!menu.children) continue;

      const isActive = menu.children.some(child => {
        if (!child.route) return false;
        return path === child.route || path.startsWith(`${child.route}/`);
      });

      if (isActive) return menu.label;
    }

    return null;
  }

  toggleProfileDropdown() {
    this.profileDropdownOpen.update(v => !v);
    if (this.profileDropdownOpen()) this.notificationsOpen.set(false);
  }

  toggleNotifications() {
    this.notificationsOpen.update(v => !v);
    if (this.notificationsOpen()) {
      this.profileDropdownOpen.set(false);
      this.loadNotifications();
    }
  }

  loadNotifications(showError = false) {
    this.api.getNotifications({ limit: 30 }).subscribe({
      next: (res: any) => {
        const data = res.success ? (res.data || {}) : {};
        this.notifications.set(data.items || []);
        this.unreadNotificationCount.set(Number(data.unread_count || 0));
      },
      error: () => {
        if (showError) showToast('error', 'Failed to load notifications.');
      }
    });
  }

  openNotification(item: any) {
    this.notificationsOpen.set(false);
    if (!item.read_at) {
      this.markNotificationRead(item);
    }
  }

  markNotificationRead(item: any) {
    this.api.markNotificationRead(Number(item.id)).subscribe({
      next: () => {
        this.notifications.update(items => items.map(current => current.id === item.id ? { ...current, read_at: current.read_at || new Date().toISOString() } : current));
        this.unreadNotificationCount.update(count => Math.max(0, count - 1));
      },
      error: () => {}
    });
  }

  markAllNotificationsRead() {
    this.api.markAllNotificationsRead().subscribe({
      next: () => {
        const now = new Date().toISOString();
        this.notifications.update(items => items.map(item => ({ ...item, read_at: item.read_at || now })));
        this.unreadNotificationCount.set(0);
      },
      error: () => showToast('error', 'Failed to update notifications.')
    });
  }

  notificationRoute(item: any): string {
    return item.route || '/admin/dashboard';
  }

  notificationIcon(item: any): string {
    const type = String(item.type || '').toLowerCase();
    if (type.includes('assignment')) return 'briefcase';
    if (type.includes('feedback')) return 'message-star';
    if (type.includes('quote')) return 'file-dollar';
    if (type.includes('leave')) return 'calendar-time';
    if (type.includes('safety')) return 'shield-exclamation';
    if (type.includes('fleet')) return 'truck';
    return 'bell';
  }

  notificationIconClass(item: any): string {
    if (item.priority === 'high') return 'bg-rose-50 text-rose-600';
    if (item.type === 'feedback') return 'bg-emerald-50 text-emerald-600';
    if (item.type === 'quote') return 'bg-sky-50 text-[#1A6FA6]';
    if (item.type === 'leave') return 'bg-amber-50 text-amber-600';
    return 'bg-slate-50 text-slate-500';
  }

  notificationTime(value: string): string {
    const date = value ? new Date(value) : null;
    if (!date || Number.isNaN(date.getTime())) return '';
    const minutes = Math.max(0, Math.floor((Date.now() - date.getTime()) / 60000));
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  }

  currentUserName(): string {
    return this.api.currentUser()?.name || 'Account';
  }

  visibleMenus(): MenuItem[] {
    return this.menus.filter(menu => !menu.officeAdminOnly || this.isOfficeOnlyAdmin());
  }

  private isOfficeOnlyAdmin(): boolean {
    const user = this.api.currentUser();
    return user?.role === 'Admin'
      && Number(user?.can_work_as_worker) === 0
      && user?.has_worker_access !== true;
  }

  currentUserRole(): string {
    const role = this.api.currentUser()?.role || 'User';
    if (role === 'Admin') return 'Administrator';
    if (role === 'Worker') return 'Field Worker';
    return role;
  }

  currentUserInitials(): string {
    return String(this.currentUserName()).split(' ').filter(Boolean).slice(0, 2).map(part => part.charAt(0).toUpperCase()).join('') || 'AD';
  }

  async clearCacheAndRefresh() {
    this.profileDropdownOpen.set(false);
    try {
      if ('caches' in window) {
        const names = await caches.keys();
        await Promise.all(names.map(name => caches.delete(name)));
      }
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map(registration => registration.unregister()));
      }
      showToast('success', 'Refreshing with the latest version...');
    } catch (error) {
      showToast('success', 'Refreshing the app...');
    } finally {
      const url = new URL(window.location.href);
      url.searchParams.set('refresh', Date.now().toString());
      window.location.replace(url.toString());
    }
  }

  installAdminApp() {
    this.profileDropdownOpen.set(false);
    this.install.install();
  }

  logout() {
    this.profileDropdownOpen.set(false);
    this.api.logout();
    this.router.navigate(['/login']);
  }
}
